/**
 * "+ Faza" — an empty Build block, an empty Deload block, or a copy of a phase that already exists.
 *
 * The canon draws this control as a dashed ghost and nothing behind it; the three entries are
 * mobile's `AddPhaseMenu`, flattened. The phone splits them across two sheet VIEWS — a root list,
 * then a phase list behind "Duplikuj istniejącą fazę…" — because a bottom sheet has one column of
 * space. A desktop popup has room for both, so they are two groups rather than two screens, and the
 * trainer never loses the two empty-phase entries to reach the copies.
 *
 * ── A new phase is APPENDED, and the menu says nothing about where ───────────────────────────
 * There is no position to choose: the tail is the only place a phase can join without shifting the
 * ones the client has already trained (`PhaseGrowthPolicy` judges position for position). A copy is
 * different — it lands directly after its source, which is what "duplicate this block" means — and
 * that is why a copy can be refused where an append never is.
 */

import { Menu } from '@base-ui/react/menu';

import type { PhaseAddMenuView } from '../model/phaseEditorView';

import menuStyles from '~/shared/ui/MenuSurface.module.css';
import styles from './PhaseBar.module.css';

export interface PhaseAddActions {
  /** Append an empty phase of this kind, minted with `view.newName`. */
  readonly add: (type: 'build' | 'deload', name: string) => void;
  /**
   * Copy the phase at this index, minted with the target's own `copyName`.
   *
   * Its answer is ignored here — the menu closes on select whatever happens, so there is no surface
   * left to stand over a confirm the copy may have raised.
   */
  readonly duplicate: (phaseIndex: number, name: string) => unknown;
}

interface PhaseAddMenuProps {
  readonly view: PhaseAddMenuView;
  readonly actions: PhaseAddActions;
}

function Entry({
  label,
  reason,
  onSelect,
}: {
  readonly label: string;
  readonly reason: string | null;
  readonly onSelect: () => void;
}) {
  return (
    <Menu.Item
      className={menuStyles.item}
      disabled={reason !== null}
      onClick={() => {
        if (reason === null) onSelect();
      }}
    >
      <span className={menuStyles.itemLabel}>
        {label}
        {reason !== null && <span className={menuStyles.reason}>{reason}</span>}
      </span>
    </Menu.Item>
  );
}

export function PhaseAddMenu({ view, actions }: PhaseAddMenuProps) {
  return (
    <Menu.Root>
      {/* The canon's dashed ghost, unchanged — except that it now acts, so it is no longer drawn
        * `--text-3` and `not-allowed`. See the stylesheet's `.addPhase` / `.addPhaseLive` note. */}
      {/* `title` beside the `aria-label`, since 17 September 2026: the label lost its word and the
        * sign alone says nothing to a pointer user who cannot hear the accessible name. The day
        * column's own adder two rows below still prints "+ Trening", so without this the screen
        * would carry two dashed adders of which only one explains itself. */}
      <Menu.Trigger
        className={styles.addPhase}
        aria-label={view.menuLabel}
        title={view.menuLabel}
      >
        {view.triggerLabel}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner className={menuStyles.positioner} sideOffset={6} align="start">
          <Menu.Popup className={menuStyles.popup}>
            <Menu.Group>
              <Menu.GroupLabel className={menuStyles.groupLabel}>{view.addGroup}</Menu.GroupLabel>
              <Entry
                label={view.buildLabel}
                reason={view.addRefusal}
                onSelect={() => {
                  actions.add('build', view.newName);
                }}
              />
              <Entry
                label={view.deloadLabel}
                reason={view.addRefusal}
                onSelect={() => {
                  actions.add('deload', view.newName);
                }}
              />
            </Menu.Group>

            <Menu.Separator className={menuStyles.separator} />

            <Menu.Group>
              <Menu.GroupLabel className={menuStyles.groupLabel}>
                {view.duplicateGroup}
              </Menu.GroupLabel>
              {view.targets.map((target) => (
                <Entry
                  key={target.index}
                  label={target.label}
                  reason={target.disabledReason}
                  onSelect={() => {
                    actions.duplicate(target.index, target.copyName);
                  }}
                />
              ))}
            </Menu.Group>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
